import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { ROUTES } from '@constants'
import ScrollToTopButton from '@components/common/ScrollToTopButton'
import { CtaBand } from '@components/marketing'
import teamImage from '@assets/images/header_accueil.png'
import phoneImage from '@assets/images/img_KUDHI.png'
import worldMap from '@assets/images/world-map-dots.png'
import waveLogo from '@assets/icons/wave.png'
import orangeLogo from '@assets/icons/Orange.png'
import mtnLogo from '@assets/icons/mtn.png'
import moovLogo from '@assets/icons/moov-money.png'

const NAVY = '#002A4E'
const GOLD = '#FFD700'
// Pastille d'icône jaune pleine
const GOLD_ICON = { background: 'linear-gradient(135deg, #FFE45C 0%, #FFD700 50%, #F5B700 100%)', color: NAVY, boxShadow: '0 8px 20px -8px rgba(245,183,0,0.7)' }

// Hook pour détecter quand un élément entre dans le viewport
function useInView(threshold = 0.15) {
  const ref = useRef(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect() } },
      { threshold }
    )
    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [threshold])
  return [ref, inView]
}

// Apparition au scroll
const Reveal = ({ as: Tag = 'div', delay = 0, className = '', children, ...rest }) => {
  const [ref, inView] = useInView()
  return (
    <Tag
      ref={ref}
      className={`anim-hidden ${inView ? 'animate-fade-in-up' : ''} ${className}`}
      style={{ animationDelay: `${delay}ms` }}
      {...rest}
    >
      {children}
    </Tag>
  )
}

const Icon = ({ d, className = 'w-6 h-6', strokeWidth = 1.8 }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={strokeWidth} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
  </svg>
)

const ICONS = {
  arrow:   'M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3',
  check:   'M4.5 12.75l6 6 9-13.5',
  image:   'M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A1.5 1.5 0 0021.75 19.5V4.5A1.5 1.5 0 0020.25 3H3.75A1.5 1.5 0 002.25 4.5v15A1.5 1.5 0 003.75 21zM15 8.25h.008v.008H15V8.25z',
  video:   'M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9A2.25 2.25 0 0013.5 5.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z',
  banner:  'M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38a1.125 1.125 0 01-1.536-.48 20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46',
  live:    'M9.348 14.651a3.75 3.75 0 010-5.303m5.304 0a3.75 3.75 0 010 5.303m-7.425 2.122a6.75 6.75 0 010-9.546m9.546 0a6.75 6.75 0 010 9.546M5.106 18.894c-3.808-3.808-3.808-9.98 0-13.789m13.788 0c3.808 3.808 3.808 9.981 0 13.79M12 12h.008v.007H12V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z',
  chat:    'M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155',
  chart:   'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z',
  shield:  'M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z',
  bolt:    'M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z',
  users:   'M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z',
  eye:     'M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
  phone:   'M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3',
}

const WORDS = ['annonces', 'produits', 'vidéos', 'bannières', 'lives']

const PAYMENTS = [
  { name: 'Wave', logo: waveLogo, available: true },
  { name: 'Orange Money', logo: orangeLogo },
  { name: 'MTN MoMo', logo: mtnLogo },
  { name: 'Moov Money', logo: moovLogo },
]

const FEATURES = [
  {
    icon: 'image', title: 'Images',
    text: "Jusqu'à 9 photos par annonce pour présenter votre offre sous tous les angles, de façon claire et professionnelle.",
    span: 'md:col-span-2',
  },
  {
    icon: 'video', title: 'Vidéos', featured: true,
    text: 'Montrez vos produits en action. Démonstrations et présentations convainquent plus vite que n\'importe quel texte.',
    span: 'md:row-span-3',
  },
  {
    icon: 'banner', title: 'Bannières',
    text: 'Mettez en avant vos promotions et nouveautés en tête de la plateforme.',
  },
  {
    icon: 'live', title: 'Lives', badge: 'Nouveau',
    text: 'Vendez en direct, échangez avec votre audience et recevez des cadeaux en temps réel.',
  },
  {
    icon: 'chat', title: 'Messagerie intégrée',
    text: 'Les acheteurs vous écrivent directement. Notifications instantanées, aucun contact perdu.',
  },
  {
    icon: 'chart', title: 'Statistiques',
    text: 'Suivez les vues, likes et commentaires de chaque annonce pour savoir ce qui marche.',
  },
]

// Icônes Material Icons (police chargée dans index.html), alignées sur le seeder des catégories
const CATEGORIES = [
  { name: 'Auto - Moto', icon: 'directions_car' },
  { name: 'Immobilier', icon: 'apartment' },
  { name: 'Smartphones', icon: 'smartphone' },
  { name: 'Informatique', icon: 'computer' },
  { name: 'Vêtements', icon: 'checkroom' },
  { name: 'Chaussures', icon: 'shopping_bag' },
  { name: 'Beauté & Cosmétique', icon: 'face' },
  { name: 'Électroménager', icon: 'kitchen' },
  { name: 'Bijoux', icon: 'diamond' },
  { name: 'Montres', icon: 'watch' },
  { name: 'Services', icon: 'build' },
  { name: 'Formations', icon: 'school' },
  { name: 'Restauration', icon: 'restaurant' },
  { name: 'Agriculture', icon: 'agriculture' },
  { name: 'Sports & Fitness', icon: 'fitness_center' },
  { name: 'Jouets & Enfants', icon: 'toys' },
  { name: 'Décorations', icon: 'celebration' },
  { name: 'Loisirs', icon: 'sports_esports' },
]

const STEPS = [
  { title: 'Créez votre compte', text: 'Inscription gratuite en quelques secondes, avec une période d\'essai offerte pour publier.' },
  { title: 'Publiez votre annonce', text: 'Ajoutez images, vidéo ou bannière, décrivez votre offre et choisissez la durée de diffusion.' },
  { title: 'Recevez des clients', text: 'Les acheteurs vous contactent directement par messagerie. Vous concluez, simplement.' },
]

const TESTIMONIALS = [
  { name: 'Marie Kouadio', company: 'Marketing Pro', text: 'En quelques jours, mes annonces vidéo ont généré plus de contacts qu\'un mois sur les réseaux sociaux.' },
  { name: 'Jean Koffi', company: 'Design Studio', text: 'Publier est rapide, le paiement Wave est instantané et les clients m\'écrivent directement. Exactement ce qu\'il me fallait.' },
  { name: 'Fatou Diallo', company: 'Creative Agency', text: 'Les bannières ont donné une vraie visibilité à notre lancement. L\'interface est claire et agréable à utiliser.' },
  { name: 'Albert Yahout', company: 'Tech Solutions', text: 'J\'ai vendu mon ordinateur en deux jours. Les acheteurs m\'ont contacté directement par la messagerie.' },
  { name: 'Sarah N\'Guessan', company: 'Business Corp', text: 'Les statistiques de vues m\'aident à savoir quelles annonces marchent le mieux. Très pratique pour ajuster mes prix.' },
  { name: 'Paul Traoré', company: 'Innovation Labs', text: 'La période d\'essai m\'a permis de tester sans risque. Aujourd\'hui je publie chaque semaine sur KUDHI.' },
  { name: 'Amadou Koné', company: 'StartUp Inc', text: 'Simple, rapide et efficace. Mes produits sont vus par beaucoup plus de clients qu\'avant.' },
  { name: 'Aïcha Bamba', company: 'Digital Media', text: 'Les lives sont une vraie révolution : je présente mes produits en direct et je vends pendant la diffusion.' },
]

const STATS = [
  { value: '500+', label: 'Utilisateurs actifs' },
  { value: '1 000+', label: 'Annonces publiées' },
  { value: '98%', label: 'Clients satisfaits' },
  { value: '7j/7', label: 'Support disponible' },
]

const initials = (name) => name.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase()

// Carrousel des témoignages : 3 cartes (desktop), 2 (tablette), 1 (mobile).
// Défilement automatique, mis en pause au survol.
function TestimonialsCarousel({ children }) {
  const items = Array.isArray(children) ? children : [children]
  const [perView, setPerView] = useState(3)
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)

  useEffect(() => {
    const update = () => setPerView(window.innerWidth >= 1024 ? 3 : window.innerWidth >= 640 ? 2 : 1)
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  const maxIndex = Math.max(0, items.length - perView)
  useEffect(() => { setIndex(i => Math.min(i, maxIndex)) }, [maxIndex])

  useEffect(() => {
    if (paused || maxIndex === 0) return
    const timer = setInterval(() => setIndex(i => (i >= maxIndex ? 0 : i + 1)), 5000)
    return () => clearInterval(timer)
  }, [paused, maxIndex])

  const go = (i) => setIndex(Math.max(0, Math.min(maxIndex, i)))

  return (
    <div className="mt-14" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="overflow-hidden -mx-3">
        <div
          className="flex transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${index * (100 / perView)}%)` }}
        >
          {items.map((child, i) => (
            <div key={i} className="flex-shrink-0 px-3" style={{ width: `${100 / perView}%` }}>
              {child}
            </div>
          ))}
        </div>
      </div>

      {maxIndex > 0 && (
        <div className="mt-8 flex items-center justify-center gap-4">
          <button
            onClick={() => go(index - 1)}
            disabled={index === 0}
            aria-label="Témoignage précédent"
            className="flex h-10 w-10 items-center justify-center rounded-md bg-white text-[#002A4E] ring-1 ring-slate-200 transition-colors hover:ring-amber-300 disabled:opacity-40"
          >
            <Icon d="M15.75 19.5L8.25 12l7.5-7.5" className="w-5 h-5" strokeWidth={2.2} />
          </button>
          <div className="flex gap-1.5">
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => go(i)}
                aria-label={`Aller au groupe ${i + 1}`}
                className={`h-2 rounded-full transition-all ${i === index ? 'w-6 bg-[#F5B700]' : 'w-2 bg-slate-300 hover:bg-slate-400'}`}
              />
            ))}
          </div>
          <button
            onClick={() => go(index + 1)}
            disabled={index === maxIndex}
            aria-label="Témoignage suivant"
            className="flex h-10 w-10 items-center justify-center rounded-md bg-white text-[#002A4E] ring-1 ring-slate-200 transition-colors hover:ring-amber-300 disabled:opacity-40"
          >
            <Icon d="M8.25 4.5l7.5 7.5-7.5 7.5" className="w-5 h-5" strokeWidth={2.2} />
          </button>
        </div>
      )}
    </div>
  )
}

const HomePage = () => {
  const [wordIndex, setWordIndex] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => setWordIndex((i) => (i + 1) % WORDS.length), 2200)
    return () => clearInterval(interval)
  }, [])

  return (
    <div className="home-font bg-white text-slate-900 overflow-x-hidden">

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #FFFCF3 0%, #FFF9E6 35%, #FFFFFF 100%)' }}>
        {/* Carte du monde à points */}
        <img
          src={worldMap}
          alt=""
          aria-hidden="true"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] max-w-none sm:w-[110%] lg:w-[95%] opacity-[0.07] pointer-events-none select-none"
        />
        <div className="absolute top-24 right-[-10%] w-[620px] h-[620px] rounded-full blur-3xl opacity-35 pointer-events-none" style={{ background: GOLD }} />
        <div className="absolute top-1/3 -left-40 w-[420px] h-[420px] rounded-full blur-3xl opacity-25 pointer-events-none" style={{ background: '#FFE58A' }} />
        <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-200 to-transparent" />

        <div className="relative w-[90%] md:w-[80%] mx-auto pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-14 items-center">
          {/* Texte */}
          <div>
            <div className="hero-badge-in inline-flex items-center gap-2 rounded-full border border-amber-200 bg-white/80 backdrop-blur px-4 py-1.5 text-sm font-medium text-slate-700 shadow-sm" style={{ opacity: 0 }}>
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping" style={{ background: GOLD }} />
                <span className="relative inline-flex h-2 w-2 rounded-full" style={{ background: GOLD }} />
              </span>
              La plateforme d&apos;annonces N°1 en Côte d&apos;Ivoire
            </div>

            <h1 className="mt-6 text-4xl sm:text-5xl xl:text-6xl font-extrabold tracking-tight leading-[1.08]" style={{ color: NAVY }}>
              Postez vos
              <span className="block h-[1.15em]">
                <span key={wordIndex} className="home-word home-highlight px-1 -mx-1" style={{ color: '#C98F00' }}>{WORDS[wordIndex]}</span>
              </span>
              touchez vos clients.
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-slate-600 max-w-xl leading-relaxed">
              Images, vidéos, bannières et lives : KUDHI rapproche vendeurs et acheteurs.
              Publiez en quelques minutes, payez avec Wave, vendez plus.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link
                to={ROUTES.REGISTER}
                className="group inline-flex items-center justify-center gap-2 rounded-md px-7 py-4 text-base font-bold shadow-lg shadow-amber-300/50 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-amber-300/60"
                style={{ background: GOLD, color: NAVY }}
              >
                Créer un compte gratuit
                <Icon d={ICONS.arrow} className="w-5 h-5 transition-transform group-hover:translate-x-1" strokeWidth={2.2} />
              </Link>
              <Link
                to={ROUTES.LIGDI}
                className="inline-flex items-center justify-center gap-2 rounded-md px-7 py-4 text-base font-semibold border border-slate-300 bg-white transition-colors hover:border-slate-400 hover:bg-slate-50"
                style={{ color: NAVY }}
              >
                Explorer KUDHI Market
              </Link>
            </div>

            <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm font-medium text-slate-600">
              {['Inscription gratuite', 'Période d\'essai offerte', 'Sans engagement'].map((t) => (
                <li key={t} className="flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full" style={{ background: GOLD, color: NAVY }}>
                    <Icon d={ICONS.check} className="w-3 h-3" strokeWidth={3} />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>

          {/* Visuel */}
          <div className="relative mx-auto w-full max-w-md flex justify-center">
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] aspect-square rounded-full opacity-60" style={{ background: `radial-gradient(circle, ${GOLD} 0%, rgba(255,215,0,0.35) 45%, transparent 70%)` }} />
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[95%] aspect-square rounded-full border border-amber-300/50" />
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] aspect-square rounded-full border border-dashed border-amber-300/40" />
            <img
              src={phoneImage}
              alt="Application KUDHI sur smartphone"
              className="relative w-auto h-[460px] sm:h-[560px] lg:h-[620px] object-contain drop-shadow-[0_35px_60px_rgba(0,0,0,0.45)] animate-fade-in"
            />

            {/* Cartes flottantes */}
            <div className="home-float absolute top-[22%] -left-2 sm:-left-10 flex items-center gap-3 rounded-md bg-white/95 backdrop-blur px-4 py-3 shadow-2xl">
              <span className="flex h-10 w-10 items-center justify-center rounded-md" style={{ background: '#E8F5E9', color: '#16A34A' }}>
                <Icon d={ICONS.eye} className="w-5 h-5" />
              </span>
              <div>
                <p className="text-xs text-slate-500">Vues aujourd&apos;hui</p>
                <p className="text-lg font-bold" style={{ color: NAVY }}>+1 284</p>
              </div>
            </div>

            <div className="home-float-slow absolute bottom-[18%] -right-2 sm:-right-10 flex items-center gap-3 rounded-md bg-white/95 backdrop-blur px-4 py-3 shadow-2xl">
              <img src={waveLogo} alt="" className="h-10 w-10 rounded-md object-cover" />
              <div>
                <p className="text-xs text-slate-500">Paiement confirmé</p>
                <p className="text-sm font-bold" style={{ color: NAVY }}>Annonce en ligne ✓</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ PAIEMENTS / CONFIANCE ============ */}
      <section className="border-b border-slate-100 bg-white">
        <div className="w-[90%] md:w-[80%] mx-auto py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <p className="text-sm font-medium text-slate-500 text-center md:text-left">
            Payez simplement avec votre mobile money
          </p>
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10">
            {PAYMENTS.map((p) => (
              <div key={p.name} className={`flex items-center gap-2.5 ${p.available ? '' : 'opacity-50 grayscale'}`}>
                <img src={p.logo} alt={p.name} className="h-9 w-9 rounded-md object-contain" />
                <div className="leading-tight">
                  <p className="text-sm font-semibold text-slate-700">{p.name}</p>
                  <p className={`text-xs ${p.available ? 'text-emerald-600 font-semibold' : 'text-slate-400'}`}>
                    {p.available ? 'Disponible' : 'Bientôt'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FONCTIONNALITÉS (BENTO) ============ */}
      <section className="py-24 lg:py-32 bg-slate-50">
        <div className="w-[90%] md:w-[80%] mx-auto">
          <Reveal className="max-w-2xl">
            <p className="text-sm font-bold uppercase tracking-widest" style={{ color: '#B8860B' }}>Fonctionnalités</p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight" style={{ color: NAVY }}>
              Tous les formats pour vendre, au même endroit.
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Choisissez le format qui met le mieux en valeur votre offre, et gérez tout depuis un seul espace.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5 md:auto-rows-fr">
            {FEATURES.map((f, i) => (
              <Reveal
                key={f.title}
                delay={i * 80}
                className={`group relative overflow-hidden rounded-md p-8 transition-all duration-300 hover:-translate-y-1 ${f.span || ''} ${
                  f.featured
                    ? 'shadow-[0_20px_50px_-20px_rgba(245,183,0,0.6)]'
                    : 'bg-white border border-slate-200/70 hover:shadow-xl hover:border-transparent'
                }`}
                {...(f.featured ? { style: { animationDelay: `${i * 80}ms`, background: 'linear-gradient(135deg, #FFE45C 0%, #FFD700 45%, #F5B700 100%)' } } : {})}
              >
                {f.featured && (
                  <>
                    <div className="absolute inset-0 home-grid-bg-light" />
                    <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 w-[120%] h-80 rounded-full bg-white/40 blur-3xl" />
                  </>
                )}
                <div className="relative flex h-full flex-col">
                  <div className="flex items-center justify-between">
                    <span
                      className="flex h-12 w-12 items-center justify-center rounded-md"
                      style={f.featured ? { background: 'rgba(255,255,255,0.7)', color: NAVY } : GOLD_ICON}
                    >
                      <Icon d={ICONS[f.icon]} />
                    </span>
                    {(f.badge || f.featured) && (
                      <span className="rounded-full px-3 py-1 text-xs font-bold" style={f.featured ? { background: NAVY, color: GOLD } : { background: GOLD, color: NAVY }}>
                        {f.badge || 'Populaire'}
                      </span>
                    )}
                  </div>
                  <h3 className="mt-6 text-xl font-bold" style={{ color: NAVY }}>
                    {f.title}
                  </h3>
                  <p className={`mt-2 leading-relaxed ${f.featured ? 'text-[#002A4E]/75' : 'text-slate-600'}`}>{f.text}</p>
                  {f.featured && (
                    <div className="relative mt-auto -mx-8 -mb-8 pt-10 flex justify-center h-[340px] md:h-[470px] overflow-hidden">
                      <img
                        src={phoneImage}
                        alt="Lecteur vidéo de l'application KUDHI"
                        className="h-[520px] md:h-[640px] w-auto max-w-none rotate-[-4deg] drop-shadow-[0_25px_40px_rgba(0,42,78,0.4)] transition-transform duration-500 group-hover:-translate-y-2"
                      />
                      <div className="absolute left-6 top-4 rounded-md border border-white/60 bg-white/70 px-4 py-3 shadow-lg backdrop-blur-md">
                        <p className="text-xs text-[#002A4E]/60">Diffusion au choix</p>
                        <p className="text-lg font-extrabold" style={{ color: NAVY }}>1 à 30 jours</p>
                      </div>
                    </div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CATÉGORIES ============ */}
      <section className="py-20 bg-white overflow-hidden">
        <Reveal className="w-[90%] md:w-[80%] mx-auto flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <p className="text-sm font-bold uppercase tracking-widest" style={{ color: '#B8860B' }}>Catégories</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: NAVY }}>
              Tout se vend sur KUDHI.
            </h2>
          </div>
          <Link to={ROUTES.LIGDI} className="group inline-flex items-center gap-2 font-semibold" style={{ color: NAVY }}>
            Voir toutes les annonces
            <Icon d={ICONS.arrow} className="w-5 h-5 transition-transform group-hover:translate-x-1" strokeWidth={2} />
          </Link>
        </Reveal>

        <Reveal className="w-[90%] md:w-[80%] mx-auto mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {CATEGORIES.map((c) => (
            <Link
              key={c.name}
              to={ROUTES.LIGDI}
              className="group flex flex-col items-center justify-center gap-4 rounded-md border border-slate-200 bg-white px-4 py-8 text-center transition-all duration-300 hover:-translate-y-1 hover:border-transparent hover:bg-[#FFD700] hover:shadow-xl hover:shadow-amber-300/50"
            >
              <span className="flex h-16 w-16 items-center justify-center rounded-md bg-gradient-to-br from-[#FFE45C] via-[#FFD700] to-[#F5B700] text-[#002A4E] shadow-[0_8px_20px_-8px_rgba(245,183,0,0.7)] transition-all duration-300 group-hover:bg-none group-hover:bg-[#002A4E] group-hover:text-[#FFD700] group-hover:shadow-lg group-hover:shadow-[#002A4E]/30">
                <span className="material-icons" style={{ fontSize: 32 }} aria-hidden="true">{c.icon}</span>
              </span>
              <span className="flex min-h-[2.5em] items-center text-[15px] font-bold leading-tight text-[#002A4E]">{c.name}</span>
            </Link>
          ))}
        </Reveal>
      </section>

      {/* ============ POURQUOI KUDHI ============ */}
      <section className="py-24 lg:py-32 bg-slate-50">
        <div className="w-[90%] md:w-[80%] mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <Reveal className="relative">
            <div className="overflow-hidden rounded-md shadow-2xl">
              <img src={teamImage} alt="Entrepreneurs utilisant KUDHI" className="w-full h-[420px] lg:h-[520px] object-cover object-[65%_center]" />
            </div>
            <div className="absolute -bottom-6 left-6 right-6 sm:left-auto sm:right-[-1.5rem] sm:w-72 rounded-md bg-white p-5 shadow-2xl">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-md" style={GOLD_ICON}>
                  <Icon d={ICONS.shield} />
                </span>
                <div>
                  <p className="font-bold" style={{ color: NAVY }}>Annonces vérifiées</p>
                  <p className="text-sm text-slate-500">Chaque publication est modérée</p>
                </div>
              </div>
            </div>
          </Reveal>

          <div>
            <Reveal>
              <p className="text-sm font-bold uppercase tracking-widest" style={{ color: '#B8860B' }}>Pourquoi KUDHI</p>
              <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight" style={{ color: NAVY }}>
                Un environnement de confiance pour acheter et vendre.
              </h2>
            </Reveal>

            <div className="mt-10 space-y-6">
              {[
                { icon: 'users', title: 'Mise en relation directe', text: 'Clients, vendeurs et entreprises échangent sans intermédiaire, directement sur la plateforme.' },
                { icon: 'bolt', title: 'Simple et rapide', text: 'Une interface intuitive pour publier en quelques minutes et trouver rapidement ce que l\'on cherche.' },
                { icon: 'shield', title: 'Sécurisé', text: 'Modération des annonces et paiement mobile money sécurisé pour négocier et conclure en toute sérénité.' },
              ].map((item, i) => (
                <Reveal key={item.title} delay={i * 100} className="flex gap-4">
                  <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-md" style={GOLD_ICON}>
                    <Icon d={ICONS[item.icon]} />
                  </span>
                  <div>
                    <h3 className="text-lg font-bold" style={{ color: NAVY }}>{item.title}</h3>
                    <p className="mt-1 text-slate-600 leading-relaxed">{item.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="w-[90%] md:w-[80%] mx-auto mt-24">
          <Reveal className="grid grid-cols-2 lg:grid-cols-4 gap-px overflow-hidden rounded-md bg-slate-200">
            {STATS.map((s) => (
              <div key={s.label} className="bg-white px-6 py-10 text-center">
                <p className="text-4xl lg:text-5xl font-extrabold tracking-tight" style={{ color: NAVY }}>{s.value}</p>
                <p className="mt-2 text-sm font-medium text-slate-500">{s.label}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* ============ COMMENT ÇA MARCHE ============ */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="w-[90%] md:w-[80%] mx-auto">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="text-sm font-bold uppercase tracking-widest" style={{ color: '#B8860B' }}>Comment ça marche</p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight" style={{ color: NAVY }}>
              Lancez-vous en 3 étapes.
            </h2>
          </Reveal>

          <div className="mt-16 grid md:grid-cols-3 gap-6 relative">
            <div className="hidden md:block absolute top-8 left-[16%] right-[16%] border-t-2 border-dashed border-slate-200" />
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 120} className="relative text-center px-4">
                <div
                  className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-md text-2xl font-extrabold shadow-lg ring-8 ring-white"
                  style={i === 1 ? { background: NAVY, color: GOLD } : { background: GOLD, color: NAVY }}
                >
                  {i + 1}
                </div>
                <h3 className="mt-6 text-xl font-bold" style={{ color: NAVY }}>{s.title}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{s.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TÉMOIGNAGES ============ */}
      <section className="py-24 lg:py-32 bg-slate-50">
        <div className="w-[90%] md:w-[80%] mx-auto">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="text-sm font-bold uppercase tracking-widest" style={{ color: '#B8860B' }}>Témoignages</p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight" style={{ color: NAVY }}>
              Ils vendent avec KUDHI.
            </h2>
          </Reveal>

          <TestimonialsCarousel>
            {TESTIMONIALS.map((t) => (
              <figure
                key={t.name}
                className="flex h-full flex-col rounded-md bg-white p-8 border border-slate-200/70 transition-shadow hover:shadow-xl"
              >
                <div className="flex gap-1" style={{ color: '#F5B700' }}>
                  {[0, 1, 2, 3, 4].map((s) => (
                    <svg key={s} className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <blockquote className="mt-5 flex-1 text-slate-700 leading-relaxed">&laquo; {t.text} &raquo;</blockquote>
                <figcaption className="mt-8 flex items-center gap-3 border-t border-slate-100 pt-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full text-sm font-bold" style={GOLD_ICON}>
                    {initials(t.name)}
                  </span>
                  <div>
                    <p className="font-bold" style={{ color: NAVY }}>{t.name}</p>
                    <p className="text-sm text-slate-500">{t.company}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </TestimonialsCarousel>
        </div>
      </section>

      <CtaBand />

      <ScrollToTopButton />
    </div>
  )
}

export default HomePage
